/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, map, shareReplay, tap} from 'rxjs/operators';

/**
 * Interface representing a language option.
 */
export interface Language {
  code: string;
  name: string;
  nativeName: string;
}

/**
 * Interface for the languages manifest file.
 */
interface LanguagesManifest {
  languages: Language[];
  generatedAt: string;
}

/**
 * Service for managing available languages in the application.
 *
 * This service loads the dynamically generated languages.json manifest file
 * that contains all available translation languages. The manifest is generated
 * at build time by scanning the assets/i18n directory.
 *
 * @example
 * ```typescript
 * constructor(private languageService: LanguageService) {
 *   this.languageService.getAvailableLanguages().subscribe(languages => {
 *     // Available languages loaded
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly http = inject(HttpClient);

  private readonly LANGUAGES_MANIFEST_PATH = '/assets/i18n/languages.json';
  private readonly DEFAULT_LANGUAGES: Language[] = [
    {code: 'en', name: 'English', nativeName: 'English'},
    {code: 'de', name: 'German', nativeName: 'Deutsch'},
  ];

  private readonly languagesSubject = new BehaviorSubject<Language[]>(
    this.DEFAULT_LANGUAGES,
  );
  private languages$: Observable<Language[]> | null = null;

  /**
   * Gets the list of available languages.
   *
   * This method loads the languages manifest file on the first call and caches
   * the result. Subsequent calls return the cached observable.
   *
   * If the manifest file cannot be loaded, falls back to default languages (en, de).
   *
   * @returns Observable that emits the array of available languages
   */
  getAvailableLanguages(): Observable<Language[]> {
    this.languages$ ??= this.http
      .get<LanguagesManifest>(this.LANGUAGES_MANIFEST_PATH)
      .pipe(
        map((manifest) => {
          // Validate: if manifest is empty, use defaults
          return manifest.languages && manifest.languages.length > 0
            ? manifest.languages
            : this.DEFAULT_LANGUAGES;
        }),
        tap((languages) => {
          this.languagesSubject.next(languages);
        }),
        catchError(() => {
          this.languagesSubject.next(this.DEFAULT_LANGUAGES);
          return of(this.DEFAULT_LANGUAGES);
        }),
        shareReplay(1),
      );
    return this.languages$;
  }

  /**
   * Gets the current list of available languages synchronously.
   *
   * Returns the cached value from the BehaviorSubject. If the languages
   * haven't been loaded yet, returns the default languages.
   *
   * @returns Array of available languages
   */
  getAvailableLanguagesSync(): Language[] {
    return this.languagesSubject.value;
  }

  /**
   * Checks if a given language code is available.
   *
   * @param langCode - Language code to check (e.g., 'en', 'de')
   * @returns True if the language is available, false otherwise
   */
  isLanguageAvailable(langCode: string): boolean {
    return this.languagesSubject.value.some((lang) => lang.code === langCode);
  }

  /**
   * Gets the display name for a language code.
   *
   * @param langCode - Language code (e.g., 'en', 'de')
   * @returns The localized language name or the code itself if not found
   */
  getLanguageName(langCode: string): string {
    const language = this.languagesSubject.value.find(
      (lang) => lang.code === langCode,
    );
    return language ? language.name : langCode.toUpperCase();
  }

  /**
   * Gets the native display name for a language code.
   *
   * @param langCode - Language code (e.g., 'en', 'de')
   * @returns The native language name or the code itself if not found
   */
  getLanguageNativeName(langCode: string): string {
    const language = this.languagesSubject.value.find(
      (lang) => lang.code === langCode,
    );
    return language ? language.nativeName : langCode.toUpperCase();
  }
}
